import { useState, useEffect, useCallback } from 'react';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { indexedDBService } from '@/services/offline/indexeddb';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export const useOfflineTechniqueNotes = (
  techniqueId: string, 
  userId: string | undefined, 
  isAuthenticated: boolean
) => {
  const { state } = useOfflineState();
  const [notes, setNotes] = useState<string>('');
  const [isSavingNotes, setSavingNotes] = useState<boolean>(false);
  const [initialNotes, setInitialNotes] = useState<string>('');

  // Fetch existing notes for this technique
  useEffect(() => {
    const fetchNotes = async () => {
      if (!isAuthenticated || !techniqueId || !userId) return;

      try {
        // Ensure IndexedDB is initialized
        await indexedDBService.initialize();

        // Always try to get from IndexedDB first (works both online and offline)
        const userTechniques = await indexedDBService.userTechniques.getByUserId(userId);
        const userTechnique = userTechniques.find(ut => ut.technique_id === techniqueId);

        if (userTechnique && userTechnique.notes !== undefined) {
          setNotes(userTechnique.notes || '');
          setInitialNotes(userTechnique.notes || '');

        }

        // If online and no cached data, fetch from server and cache it
        if (state.isOnline && (!userTechnique || userTechnique.notes === undefined)) {
          const { data, error } = await supabase
            .from('user_techniques')
            .select('notes')
            .eq('user_id', userId)
            .eq('technique_id', techniqueId)
            .maybeSingle();

          if (error) throw error;

          if (data) {
            const serverNotes = data.notes || '';
            setNotes(serverNotes);
            setInitialNotes(serverNotes);


            // Cache the notes in IndexedDB for offline use
            if (userTechnique) {
              await indexedDBService.userTechniques.update(userTechnique.id, {
                ...userTechnique,
                notes: serverNotes
              });
            } else {
              await indexedDBService.userTechniques.create({
                user_id: userId,
                technique_id: techniqueId,
                notes: serverNotes,
                proficiency_level: 'learning',
                times_practiced: 0,
                is_favorite: 0 // Convert to number for IndexedDB
              });
            }
          }
        }
      } catch (error: unknown) {
        console.error('Error fetching technique notes:', error);
      }
    };

    fetchNotes();
  }, [techniqueId, isAuthenticated, userId, state.isOnline]);

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  const saveNotes = useCallback(async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to save notes",
        variant: "destructive"
      });
      return;
    }
    
    if (!techniqueId || !userId) return;
    
    // Don't save if notes haven't changed
    if (notes === initialNotes) return;
    
    setSavingNotes(true);
    
    try {
      // Ensure IndexedDB is initialized
      await indexedDBService.initialize();

      if (state.isOnline) {
        // Online: Save to Supabase and update IndexedDB
        const { data: existingData } = await supabase
          .from('user_techniques')
          .select('id')
          .eq('user_id', userId)
          .eq('technique_id', techniqueId)
          .maybeSingle();
        
        if (existingData) {
          // Update existing record
          const { error } = await supabase
            .from('user_techniques')
            .update({ notes })
            .eq('id', existingData.id);
          
          if (error) throw error;
        } else {
          // Create new record - but don't set is_favorite to avoid unwanted likes
          const { error } = await supabase
            .from('user_techniques')
            .insert({
              user_id: userId,
              technique_id: techniqueId,
              notes,
              is_favorite: false // Explicitly set to false to avoid unwanted likes
            });
          
          if (error) throw error;
        }

        // Update IndexedDB cache
        const userTechniques = await indexedDBService.userTechniques.getByUserId(userId);
        const existingUserTechnique = userTechniques.find(ut => ut.technique_id === techniqueId);

        if (existingUserTechnique) {
          await indexedDBService.userTechniques.update(existingUserTechnique.id, {
            ...existingUserTechnique,
            notes
          });

        } else {
          await indexedDBService.userTechniques.create({
            user_id: userId,
            technique_id: techniqueId,
            notes,
            proficiency_level: 'learning',
            times_practiced: 0,
            is_favorite: 0 // Convert to number for IndexedDB
          });

        }
      } else {
        // Offline: Save to IndexedDB only and queue for sync
        const userTechniques = await indexedDBService.userTechniques.getByUserId(userId);
        const existingUserTechnique = userTechniques.find(ut => ut.technique_id === techniqueId);
        
        if (existingUserTechnique) {
          await indexedDBService.userTechniques.update(existingUserTechnique.id, {
            ...existingUserTechnique,
            notes
          });
        } else {
          await indexedDBService.userTechniques.create({
            user_id: userId,
            technique_id: techniqueId,
            notes,
            proficiency_level: 'learning',
            times_practiced: 0,
            is_favorite: 0 // Convert to number for IndexedDB
          });
        }
      }
      
      setInitialNotes(notes);
      toast({
        title: "Notes saved",
        description: state.isOnline 
          ? "Your notes have been saved successfully" 
          : "Your notes have been saved offline and will sync when you're back online"
      });
    } catch (error: unknown) {
      toast({
        title: "Error saving notes",
        description: error instanceof Error ? error.message : "An error occurred while saving your notes",
        variant: "destructive"
      });
    } finally {
      setSavingNotes(false);
    }
  }, [notes, initialNotes, isAuthenticated, techniqueId, userId, state.isOnline]);

  return {
    notes,
    isSavingNotes,
    initialNotes,
    handleNotesChange,
    saveNotes
  };
};
